import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
const MLBHeaderStats = ({ metadata, totalGames, games }) => {
    const { user } = useAuth();
    const [userPerformance, setUserPerformance] = useState(null);
    const [isLoadingPerformance, setIsLoadingPerformance] = useState(true);
    // Fetch user performance data
    useEffect(() => {
        const fetchUserPerformance = async () => {
            if (!user) {
                setIsLoadingPerformance(false);
                return;
            }
            try {
                const token = localStorage.getItem('sharpshot_auth_token');
                const response = await fetch('/api/bets/performance?timeframe=all', {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    setUserPerformance(data.data);
                }
                else {
                    console.error('Failed to fetch user performance:', response.status, response.statusText);
                    // Set empty performance data so stats still show
                    setUserPerformance({
                        overall: {
                            totalBets: 0,
                            wonBets: 0,
                            lostBets: 0,
                            winRate: 0,
                            totalWagered: 0,
                            totalReturns: 0,
                            totalProfit: 0,
                            roi: 0
                        },
                        recentBets: []
                    });
                }
            }
            catch (error) {
                console.error('Error fetching user performance:', error);
                // Set empty performance data so stats still show
                setUserPerformance({
                    overall: {
                        totalBets: 0,
                        wonBets: 0,
                        lostBets: 0,
                        winRate: 0,
                        totalWagered: 0,
                        totalReturns: 0,
                        totalProfit: 0,
                        roi: 0
                    },
                    recentBets: []
                });
            }
            finally {
                setIsLoadingPerformance(false);
            }
        };
        fetchUserPerformance();
    }, [user]);
    // Calculate current streak from recent bets
    const calculateCurrentStreak = (recentBets) => {
        if (!recentBets || recentBets.length === 0)
            return { streak: 0, isWinStreak: true };
        let streak = 0;
        const firstBetStatus = recentBets[0].status;
        // Only count settled bets for streak calculation
        const settledBets = recentBets.filter(bet => bet.status === 'won' || bet.status === 'lost');
        if (settledBets.length === 0)
            return { streak: 0, isWinStreak: true };
        const currentStatus = settledBets[0].status;
        for (const bet of settledBets) {
            if (bet.status === currentStatus) {
                streak++;
            }
            else {
                break;
            }
        }
        return { streak, isWinStreak: currentStatus === 'won' };
    };
    // Calculate confidence level counts and performance metrics from actual games data
    const getConfidenceCounts = () => {
        const confidenceCounts = {
            HIGH: 0,
            MEDIUM_PLUS: 0,
            MEDIUM: 0,
            LOW: 0,
            NO_BET: 0
        };
        // Count actual confidence levels from games (using new structure)
        games.forEach(game => {
            // Map new structure to old confidence levels
            let level = 'NO_BET';
            if (game.professional_bet_decision?.should_bet && game.professional_bet_decision?.bet_tier) {
                level = game.professional_bet_decision.bet_tier;
            }
            else if (!game.professional_bet_decision?.should_bet) {
                // For now, map non-betting games to NO_BET, but we could use confidence_score to categorize
                const confidence = game.professional_bet_decision?.confidence_score || 0;
                if (confidence > 0.7)
                    level = 'HIGH';
                else if (confidence > 0.65)
                    level = 'MEDIUM_PLUS';
                else if (confidence > 0.6)
                    level = 'MEDIUM';
                else if (confidence > 0.55)
                    level = 'LOW';
                else
                    level = 'NO_BET';
            }
            if (level in confidenceCounts) {
                confidenceCounts[level]++;
            }
        });
        // Calculate accuracy and ROI from actual data
        const bettableGames = games.filter(game => game.professional_bet_decision?.should_bet);
        const totalAccuracy = (metadata.model_performance.ensemble?.accuracy || 0.65) * 100;
        const avgExpectedROI = bettableGames.length > 0
            ? bettableGames.reduce((sum, game) => {
                const roi = game.professional_bet_decision?.expected_roi || 0;
                return sum + roi;
            }, 0) / bettableGames.length
            : 0;
        // Calculate max earnings based on user bankroll and high confidence bets
        const userBankroll = user?.bankroll || 1000; // Default $1000 if no bankroll set
        const betSizePercentage = 0.05; // 5% of bankroll per bet (conservative)
        const betSize = userBankroll * betSizePercentage;
        const averageROI = 0.08; // 8% average ROI for high confidence bets
        const highConfidenceBets = confidenceCounts.HIGH + confidenceCounts.MEDIUM_PLUS;
        const maxEarnings = highConfidenceBets * betSize * averageROI;
        // Use real user performance data if available
        let userStats = {
            totalBets: 0,
            winRate: 0,
            currentStreak: 0,
            isWinStreak: true,
            totalProfitLoss: 0,
            unitsWonLost: 0,
            roi: 0
        };
        if (userPerformance && !isLoadingPerformance) {
            const streakData = calculateCurrentStreak(userPerformance.recentBets);
            const unitSize = userBankroll * 0.01;
            userStats = {
                totalBets: userPerformance.overall.totalBets || 0,
                winRate: isNaN(userPerformance.overall.winRate) ? 0 : userPerformance.overall.winRate,
                currentStreak: streakData.streak,
                isWinStreak: streakData.isWinStreak,
                totalProfitLoss: userPerformance.overall.totalProfit || 0,
                unitsWonLost: isNaN(userPerformance.overall.totalProfit / unitSize) ? 0 : userPerformance.overall.totalProfit / unitSize,
                roi: isNaN(userPerformance.overall.roi) ? 0 : userPerformance.overall.roi
            };
        }
        return {
            high: highConfidenceBets, // Combine high confidence tiers
            medium: confidenceCounts.MEDIUM,
            low: confidenceCounts.LOW,
            noBet: confidenceCounts.NO_BET,
            accuracy: totalAccuracy.toFixed(1),
            roi: avgExpectedROI.toFixed(1),
            maxEarnings: maxEarnings,
            // Real user performance metrics
            totalBets: userStats.totalBets,
            winRate: userStats.winRate.toFixed(1),
            currentStreak: userStats.currentStreak,
            isWinStreak: userStats.isWinStreak,
            totalProfitLoss: userStats.totalProfitLoss,
            unitsWonLost: userStats.unitsWonLost
        };
    };
    const stats = getConfidenceCounts();
    return (_jsx("div", { className: "card mb-4", style: { backgroundColor: 'var(--card-bg)', borderColor: 'var(--card-border)' }, children: _jsx("div", { className: "card-body", children: _jsxs("div", { className: "row", children: [_jsxs("div", { className: "col-md-8", children: [_jsxs("h2", { className: "card-title mb-3", children: [_jsx("i", { className: "fas fa-baseball-ball text-primary me-2" }), "Today's MLB Predictions"] }), _jsxs("p", { className: "card-text", children: [totalGames, " games analyzed with ", stats.high, " high-confidence picks, ", stats.medium, " medium-confidence picks, ", stats.low, " low-confidence selections, and ", stats.noBet, " no-bet recommendations."] })] }), _jsx("div", { className: "col-md-4", children: _jsxs("div", { className: "row g-3", children: [_jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "h3 mb-0 text-primary", children: isLoadingPerformance ? (_jsx("i", { className: "fas fa-spinner fa-spin" })) : (stats.totalBets) }), _jsx("small", { className: "text-muted", children: "Total Bets" })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: "h3 mb-0 text-success", children: [stats.winRate, "%"] }), _jsx("small", { className: "text-muted", children: "Win Rate" })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: `h5 mb-0 ${stats.isWinStreak ? 'text-success' : 'text-danger'}`, children: isLoadingPerformance ? (_jsx("i", { className: "fas fa-spinner fa-spin" })) : stats.currentStreak > 0 ? (`${stats.isWinStreak ? 'W' : 'L'}${stats.currentStreak}`) : ('-') }), _jsx("small", { className: "text-muted", children: "Current Streak" })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: `h5 mb-0 ${stats.totalProfitLoss >= 0 ? 'text-success' : 'text-danger'}`, children: [stats.totalProfitLoss >= 0 ? '+' : '', "$", Math.round(stats.totalProfitLoss)] }), _jsx("small", { className: "text-muted", children: "Total P/L" })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: `h5 mb-0 ${stats.unitsWonLost >= 0 ? 'text-success' : 'text-danger'}`, children: [stats.unitsWonLost >= 0 ? '+' : '', stats.unitsWonLost.toFixed(1), "U"] }), _jsx("small", { className: "text-muted", children: "Units Won/Lost" })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "h5 mb-0 text-info", children: stats.high }), _jsx("small", { className: "text-muted", children: "High Value Bets" })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: "h5 mb-0 text-success", children: ["+", stats.roi, "%"] }), _jsx("small", { className: "text-muted", children: "Expected ROI" })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: "h5 mb-0 text-primary", children: ["$", Math.round(stats.maxEarnings)] }), _jsx("small", { className: "text-muted", children: "Max Earnings" })] }) })] }) })] }) }) }));
};
export default MLBHeaderStats;
